import ConfirmationModal from "../../../components/ConfirmationModal";
import { Stagger, StaggerItem } from "../../../../motion";

// Shown before the Edit Client form when the client is used beyond this
// quotation: other quotations point at the same user, and/or the user has an
// account on the website. `clientImpact` comes from fetchQuotationClientImpact.

const ACCOUNT_TYPE_LABELS = {
  individual: "an individual",
  business: "a business",
};

// Same wording as the status badge on the quotation list/detail page.
const getQuotationStatusLabel = (quotation) => {
  if (quotation.paymentStatus === "CHARGED") return "Charged";
  if (quotation.paymentStatus === "CHARGE_INITIATED") return "Charge Processing";
  if (quotation.paymentStatus === "READY_TO_CHARGE") return "Ready To Charge";
  if (quotation.paymentStatus === "CHARGE_FAILED") return "Charge Failed";
  if (quotation.paymentStatus === "PAID") return "Paid";
  if (quotation.quotationStatus === "DRAFT") return "Draft";
  if (quotation.quotationStatus === "ACTIVE") return "Active - Not paid";
  if (quotation.quotationStatus === "EXPIRED") return "Expired";
  if (quotation.quotationStatus === "UNSAVED") return "Unsaved";
  return "";
};

const ImpactSection = ({ title, children }) => (
  <StaggerItem className="tw:rounded-md tw:bg-[#FFF8E8] tw:p-4">
    <p className="tw:!mb-1 tw:!text-[14px] tw:!font-[600] tw:!text-[#4E4E4E]">{title}</p>
    <div className="tw:text-[13px] tw:text-[#7C7B7B]">{children}</div>
  </StaggerItem>
);

const OtherQuotationList = ({ otherQuotations, otherQuotationCount }) => {
  const hiddenQuotationCount = otherQuotationCount - otherQuotations.length;

  return (
    <ul className="tw:!mt-2 tw:!mb-0 tw:!max-h-40 tw:!overflow-y-auto tw:!ps-4">
      {otherQuotations.map((quotation) => (
        <li key={quotation._id}>
          <a
            href={`/admin/quotations/create/${quotation._id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tw:!text-[#D2AF61] tw:!underline"
          >
            {quotation.quotationID || "(no Quotation ID yet)"}
          </a>
          {getQuotationStatusLabel(quotation) && ` — ${getQuotationStatusLabel(quotation)}`}
        </li>
      ))}
      {hiddenQuotationCount > 0 && <li>and {hiddenQuotationCount} more</li>}
    </ul>
  );
};

const EditClientImpactConfirmationModal = ({ show, clientImpact, onCancel, onConfirm }) => {
  if (!clientImpact) return null;

  const {
    accountType,
    isRegisteredAccount,
    otherQuotationCount,
    otherQuotations = [],
    existingBookingCount,
  } = clientImpact;

  return (
    <ConfirmationModal
      show={show}
      size="modal-lg"
      title="This client is used outside this quotation"
      description="Changes you save will also apply to everything listed below."
      confirmLabel="Continue to edit"
      onCancel={onCancel}
      onConfirm={onConfirm}
    >
      <Stagger onMount className="tw:flex tw:flex-col tw:gap-3 tw:text-left">
        {otherQuotationCount > 0 && (
          <ImpactSection
            title={`Used in ${otherQuotationCount} other quotation${otherQuotationCount > 1 ? "s" : ""}`}
          >
            These quotations will show the new client details.
            <OtherQuotationList
              otherQuotations={otherQuotations}
              otherQuotationCount={otherQuotationCount}
            />
          </ImpactSection>
        )}

        {isRegisteredAccount && (
          <ImpactSection
            title={`Registered as ${ACCOUNT_TYPE_LABELS[accountType] || "a"} user on the website`}
          >
            The billing information of their account is updated too. The customer
            sees it on My Page, and their next bookings start from it.
          </ImpactSection>
        )}

        {existingBookingCount > 0 && (
          <ImpactSection
            title={`${existingBookingCount} existing booking${existingBookingCount > 1 ? "s" : ""}`}
          >
            Existing bookings keep the billing details they were made with — they
            are not changed.
          </ImpactSection>
        )}
      </Stagger>
    </ConfirmationModal>
  );
};

export default EditClientImpactConfirmationModal;
